import React, { useMemo } from "react";
import Highlighter from "react-highlight-words";

function formatCellValue(cellValue, keyItem, config) {
    // Custom formatter per column
    const customFormatter = config[keyItem]?.formatter;
    if (typeof customFormatter === "function") {
        const formatted = customFormatter(cellValue, config[keyItem]);
        if (Array.isArray(formatted)) {
            return formatted;
        }
        if (formatted == null) {
            return ["-"];
        }
        const str = String(formatted);
        return [str.length > 0 ? str : "-"];
    }

    // Null or undefined
    if (cellValue == null) {
        return ["-"];
    }

    // Array: flatten each item
    if (Array.isArray(cellValue)) {
        if (cellValue.length === 0) return ["-"];
        return cellValue.flatMap(item => formatCellValue(item, keyItem, config));
    }

    // Primitive types
    if (["string", "number", "boolean"].includes(typeof cellValue)) {
        const str = String(cellValue);
        return [str.length > 0 ? str : "-"];
    }

    // Object: check if empty (all values null or empty)
    if (typeof cellValue === "object") {
        const values = Object.values(cellValue);
        const allEmpty = values.length === 0 || values.every(v => v == null || String(v).trim() === "");
        if (allEmpty) {
            return ["-"];
        }
        const { hasDisplayName, appendColumnKey } = config[keyItem] || {};
        const displayField = hasDisplayName ? "display_name" : "name";
        let base = cellValue[displayField] != null && String(cellValue[displayField]).trim() !== "" ? String(cellValue[displayField]) : "-";
        if (appendColumnKey && cellValue[appendColumnKey]) {
            base += ` (${cellValue[appendColumnKey]})`;
        }
        return [base];
    }

    // Fallback: toString
    return [String(cellValue)];
}

export const TableCell = React.memo(({ rowData, keyItem, config, searchKeysArray, filterHighlighter }) => {
    // Determine if highlighting should be applied
    const shouldHighlight = filterHighlighter(config[keyItem]?.searchKey, keyItem);

    // Extract raw cell value, supporting nested paths via accessor
    const rawValue = useMemo(() => {
        const accessor = config[keyItem]?.accessor;
        return typeof accessor === "function" ? accessor(rowData) : rowData[keyItem];
    }, [rowData, keyItem, config]);

    // Compute all text fragments for this cell (accessor already returns display text)
    const texts = useMemo(() => {
        if (typeof config[keyItem]?.accessor === "function") {
            if (rawValue == null) return ["-"];
            const str = String(rawValue);
            return [str.length > 0 ? str : "-"];
        }
        return formatCellValue(rawValue, keyItem, config);
    }, [rawValue, keyItem, config]);

    return (
        <>
            {texts.map((text, idx) => (
                <Highlighter
                    key={idx}
                    className="text-highlighter"
                    unhighlightClassName="text-unhighlight"
                    searchWords={shouldHighlight ? searchKeysArray : []}
                    textToHighlight={text}
                    data-tip={text}
                    data-effect="solid"
                    data-place="bottom"
                    data-for={`table`}
                    data-class="rc-tooltip-custom-class"
                />
            ))}
        </>
    );
});
